import {
  useWSModalCustom,
  WSAlert,
  WSInfoBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { Batch } from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";

import { ErrorAlert } from "@components/ErrorAlert";
import useSnackbar from "../../../../hooks/useSnackbar";
import { getApiErrorMessage } from "../../../../v3/utils/apiError";
import { useFlatfileUploadJobStatus } from "../../hooks/useFlatfileUploadJobStatus";
import { selectorImportBatchUploadedFilename } from "../../selectors/selectorImportBatchUploadedFilename";

type Props = {
  batch: Batch;
  isEmptyBatch: boolean;
  /** Singular noun for what the batch imports, e.g. "Payable". */
  entityLabel: string;
  /** Shown in the info box, e.g. "This batch has line items but …". */
  description: string;
  /** Shown instead when the batch holds nothing yet. */
  emptyBatchDescription: string;
} & WSModalCustomProps<boolean>;

const ModalConfirmImportBatchDelete: React.FC<Props> = props => {
  const { success, warning } = useSnackbar();
  const [deleteBatch, deleteBatchMeta] =
    queriesBatches.deleteBatch.useMutation();
  const { isFailed: isUploadFailed } = useFlatfileUploadJobStatus(props.batch);

  const onDelete = async () => {
    await deleteBatch(
      { batchId: props.batch.id as string },
      {
        onSuccess: () => {
          success(
            `${selectorImportBatchUploadedFilename({
              batch: props.batch,
              entityLabel: props.entityLabel
            })} successfully deleted.`
          );
          props.onClose(true);
        },
        onError: error => {
          warning(
            `Failed to delete: ${
              getApiErrorMessage(error) || "Something went wrong!"
            }`
          );
        }
      }
    );
  };

  return (
    <WSModal
      title="Delete batch?"
      size="S"
      onClose={() => props.onClose(false)}
      actions={{
        alignment: "fill",
        sortButtons: false,
        buttons: [
          {
            label: "Cancel",
            kind: "Tertiary",
            onClick: () => props.onClose(false)
          },
          {
            label: "Delete",
            destructive: true,
            loading: deleteBatchMeta.isLoading,
            onClick: onDelete
          }
        ]
      }}
    >
      <WSList gap="L">
        <WSText.ParagraphSm weight="book" color="gray600">
          Are you sure you want to delete this batch?
        </WSText.ParagraphSm>

        <WSInfoBox>
          {props.isEmptyBatch ? props.emptyBatchDescription : props.description}
        </WSInfoBox>

        {isUploadFailed && (
          <WSAlert
            theme="warning"
            icon="alert"
            name="deleteBatchWithErrors"
            title="Warning"
          >
            This import has errors.
            <br />
            Deleting it may complicate the investigation, as it permanently
            removes information that might be needed to determine the cause.
          </WSAlert>
        )}

        <ErrorAlert context="BulkBatchUpdate" error={deleteBatchMeta.error} />
      </WSList>
    </WSModal>
  );
};

export const useModalConfirmImportBatchDelete = () =>
  useWSModalCustom(ModalConfirmImportBatchDelete);
